"use client";

import type { RefObject } from "react";
import {
	InteractiveMediaBarsTrack,
	mapInteractiveAudioSegmentsToSeekSegments,
	resolveInteractiveMediaBarsTheme,
	TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX,
} from "../interactive-media-player/interactive-media-bars-track";
import { InteractiveAudioWaveformTrack } from "./interactive-audio-waveform-track";
import type {
	InteractiveAudioProgressTrackVariant,
	InteractiveAudioSegmentRegion,
	InteractiveAudioSegmentVisualState,
	InteractiveMediaBarsLayout,
} from "./types";

export interface InteractiveAudioPlayerControlledProps {
	mode: "controlled";
	trackVariant?: InteractiveAudioProgressTrackVariant;
	barsLayout?: InteractiveMediaBarsLayout;
	progressPercent: number;
	isPlaying?: boolean;
	isLoading?: boolean;
	isPreparingPlayback?: boolean;
	/** When false, the waveform track is not rendered (voice-sheet idle pattern). */
	isActive?: boolean;
	volumePercent?: number;
	maxSeekPercent?: number;
	furthestConsumedPercent?: number;
	continuationPercent?: number;
	scrubClampPercent?: number;
	segments?: InteractiveAudioSegmentRegion[];
	segmentStates?: Record<string, InteractiveAudioSegmentVisualState>;
	showParticles?: boolean;
	ambientParticleColor?: string;
	trackGlowColor1?: string;
	trackGlowColor2?: string;
	trackGlow?: boolean;
	interactive?: boolean;
	activeAudioRef?: RefObject<HTMLAudioElement | null>;
	onScrubPause?: () => void;
	onSeekCommit?: (percent: number) => void;
	onSeekPreview?: (percent: number) => void;
	onSeekRejected?: () => void;
	className?: string;
	"aria-label"?: string;
	seekEpoch?: number;
}

export function InteractiveAudioPlayerControlled({
	trackVariant = "waveform",
	barsLayout = "wide",
	progressPercent,
	isPlaying = false,
	isPreparingPlayback = false,
	isLoading = false,
	isActive = true,
	volumePercent = 100,
	maxSeekPercent = 100,
	furthestConsumedPercent = maxSeekPercent,
	continuationPercent,
	scrubClampPercent,
	segments = [],
	segmentStates = {},
	showParticles = true,
	ambientParticleColor,
	trackGlowColor1,
	trackGlowColor2,
	trackGlow = true,
	interactive = true,
	activeAudioRef,
	onScrubPause,
	onSeekCommit = () => {},
	onSeekPreview,
	onSeekRejected,
	className,
	"aria-label": ariaLabel = "Audio playback position",
	seekEpoch = 0,
}: InteractiveAudioPlayerControlledProps) {
	const handleScrubPause = () => {
		onScrubPause?.();
	};

	if (trackVariant === "bars") {
		const theme = resolveInteractiveMediaBarsTheme({
			barsLayout,
			trackGlowColor1,
			trackGlowColor2,
			ambientParticleColor,
		});
		const seekSegments =
			segments.length > 0
				? mapInteractiveAudioSegmentsToSeekSegments(segments)
				: [];
		return (
			<InteractiveMediaBarsTrack
				theme={theme}
				value={progressPercent}
				maxSeekPercent={maxSeekPercent}
				furthestConsumedPercent={furthestConsumedPercent}
				continuationPercent={continuationPercent}
				scrubClampPercent={scrubClampPercent}
				segments={seekSegments}
				interactive={interactive && isActive}
				trackGlow={trackGlow}
				showAmbientParticles={showParticles}
				roundedFull={barsLayout !== "narrow"}
				showThumb={false}
				showSeekPin
				thumbDiameterPx={TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX}
				onPause={handleScrubPause}
				onSeekCommit={onSeekCommit}
				onSeekPreview={onSeekPreview}
				onSeekRejected={onSeekRejected}
				seekEpoch={seekEpoch}
				className={className}
				aria-label={ariaLabel}
			/>
		);
	}

	return (
		<InteractiveAudioWaveformTrack
			audioRef={activeAudioRef}
			isActive={isActive}
			isPlaying={isPlaying}
			isPreparingPlayback={isPreparingPlayback}
			isLoading={isLoading}
			progressPercent={progressPercent}
			volumePercent={volumePercent}
			maxSeekPercent={maxSeekPercent}
			furthestConsumedPercent={furthestConsumedPercent}
			continuationPercent={continuationPercent}
			scrubClampPercent={scrubClampPercent}
			segments={segments}
			segmentStates={segmentStates}
			showParticles={showParticles}
			ambientParticleColor={ambientParticleColor}
			trackGlowColor1={trackGlowColor1}
			trackGlowColor2={trackGlowColor2}
			interactive={interactive}
			ariaLabel={ariaLabel}
			onScrubPause={handleScrubPause}
			onSeekCommit={onSeekCommit}
			onSeekPreview={onSeekPreview}
			onSeekRejected={onSeekRejected}
			seekEpoch={seekEpoch}
			className={className}
		/>
	);
}
